import type { TOC } from '@ember/component/template-only'
import type ConfirmRoute from 'teacher-ui-client/manage/import/students/confirm/route'
import type ImportStudentsConfirmController from 'teacher-ui-client/manage/import/students/confirm/controller'
import { t } from 'ember-intl'
import { or } from 'ember-truth-helpers'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { UiButtonLink } from 'teacher-ui-client/components/ui-button/link'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { on } from '@ember/modifier'
import { gradeName } from 'teacher-ui-client/helpers/grade-name'
import { FidgetLoadingIndicatorComponent } from '@blakeelearning/fidget/components/fidget/loading-indicator'
import RouteTemplate from 'ember-route-template'
import { InlineAlert, AlertType, InlineAlertSubtitle } from 'teacher-ui-client/components/inline-alert'

interface Signature {
  Args: {
    model: ModelFor<ConfirmRoute>
    controller: ImportStudentsConfirmController
  }
}
export const ManageImportStudentsConfirmPage: TOC<Signature> = <template>
  {{#if @controller.displayWarning}}
    <InlineAlert @type={{AlertType.Warning}} role="alert" class="mb-6">
      <InlineAlertSubtitle>
        <ul class="mb-6 ml-2 list-disc space-y-3">
          {{#if @controller.studentsNameChanged}}
            <li>{{t "manage.studentCsv.studentsNameChanged"}}</li>
          {{/if}}
          {{#if @controller.duplicateStudentsInSource}}
            <li>{{t "manage.studentCsv.duplicateStudentsInSource"}}</li>
          {{/if}}
          {{#if @controller.duplicateStudentsInDestination}}
            <li>{{t "manage.studentCsv.duplicateStudentsInDestination"}}</li>
          {{/if}}
        </ul>

        {{#if (or @controller.duplicateStudentsInSource @controller.duplicateStudentsInDestination)}}
          <p>{{t "manage.studentCsv.duplicateStudentsSolution"}}</p>
        {{/if}}
      </InlineAlertSubtitle>
    </InlineAlert>
  {{/if}}

  <InlineAlert @type={{AlertType.Information}} role="alert">
    <InlineAlertSubtitle>
      <p data-test-filename>{{t
          "manage.studentCsv.validFile"
          filename=@controller.filename
          count=@controller.studentsCount
          htmlSafe=true
        }}</p>
      <p>{{t "manage.studentCsv.validFileNext"}}</p>
    </InlineAlertSubtitle>
  </InlineAlert>

  <table class="mt-6 w-full">
    <thead class="import-table-header">
      <tr>
        {{#if @controller.displayWarning}}
          <th class="import-table-cell import-table-header-cell w-16"></th>
        {{/if}}
        <th class="import-table-cell import-table-header-cell w-24 text-center">{{t
            "manage.bulkActions.csv.lineNo"
          }}</th>
        <th class="import-table-cell import-table-header-cell capitalize">{{t
            "manage.studentCsv.attribute.first-name"
          }}</th>
        <th class="import-table-cell import-table-header-cell capitalize">{{t
            "manage.studentCsv.attribute.last-name"
          }}</th>
        <th class="import-table-cell import-table-header-cell capitalize">{{t "grade.label"}}</th>
        <th class="import-table-cell import-table-header-cell capitalize">{{t
            "manage.studentCsv.attribute.external-id"
          }}</th>
        {{#if @controller.isSubco}}
          <th class="import-table-cell import-table-header-cell capitalize">{{t
              "manage.studentCsv.attribute.teacher-email"
            }}</th>
        {{/if}}
        <th class="import-table-cell import-table-header-cell border-0">
          <span class="capitalize">{{t "manage.studentCsv.schoolClassName"}}</span>
        </th>
      </tr>
    </thead>
    <tbody class="import-table-body">
      {{#each @controller.candidateStudents as |student|}}
        <tr data-test-student-row>
          {{#if @controller.displayWarning}}
            <td class="import-table-cell import-table-body-cell text-center">
              {{#if (or student.duplicateSource student.duplicateDestination student.studentNameChange)}}
                <PhosphorIcon @icon="phosphor-warning-fill" class="text-juicy-orange-300" data-test-warning-icon />
              {{/if}}
            </td>
          {{/if}}
          <td class="import-table-cell import-table-body-cell text-center">{{student.csvRow}}</td>
          <td class="import-table-cell import-table-body-cell">{{student.firstName}}</td>
          <td class="import-table-cell import-table-body-cell">{{student.lastName}}</td>
          <td class="import-table-cell import-table-body-cell">{{gradeName student.gradePosition}}</td>
          <td class="import-table-cell import-table-body-cell">{{student.externalId}}</td>
          {{#if @controller.isSubco}}
            <td class="import-table-cell import-table-body-cell">{{student.teacherEmail}}</td>
          {{/if}}
          <td class="import-table-cell import-table-body-cell">
            {{#if student.schoolClassName}}
              {{student.schoolClassName}}
            {{else}}
              {{t "manage.noClassAssigned"}}
            {{/if}}
          </td>
        </tr>
      {{/each}}
    </tbody>
  </table>

  <div class="mt-8 flex">
    <UiButtonLink
      class="mr-8 whitespace-nowrap"
      @appearance={{ButtonAppearance.muted}}
      {{on "click" @controller.cancel}}
    >
      {{t "manage.bulkActions.csv.cancel"}}
    </UiButtonLink>
    <UiButton
      data-test-complete-import
      class="whitespace-nowrap"
      disabled={{@controller.isImporting}}
      @appearance={{ButtonAppearance.regular}}
      {{on "click" @controller.submit}}
    >
      {{#if @controller.isImporting}}
        {{t "manage.studentCsv.importUploading"}}
      {{else}}
        {{t "manage.bulkActions.csv.submit"}}
      {{/if}}
    </UiButton>
    <FidgetLoadingIndicatorComponent
      class="mx-3 my-auto inline-block"
      @show={{@controller.isImporting}}
      @appearance="small"
      @overlay={{false}}
    />
  </div>
</template>

export default RouteTemplate(ManageImportStudentsConfirmPage)
